import { createFileRoute, Outlet, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { LogOut } from "lucide-react";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { AccountTypeOnboarding } from "@/components/AccountTypeOnboarding";
import { WelcomeOnboarding } from "@/components/WelcomeOnboarding";
import { AssistantChat } from "@/components/AssistantChat";
import { NotificationsBell } from "@/components/NotificationsBell";
import { WhiteLabelProvider } from "@/hooks/useWhiteLabel";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { useI18n } from "@/lib/i18n";

export const Route = createFileRoute("/_app")({
  component: AppLayout,
});

function AppLayout() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { user, loading, signOut } = useAuth();

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate({ to: "/auth" });
      return;
    }
    // Gate dashboard until email is verified. OAuth users (Google) are
    // auto-confirmed by the provider so email_confirmed_at is set.
    if (!user.email_confirmed_at) {
      navigate({ to: "/verify-email", search: { email: user.email ?? "" } });
    }
  }, [loading, user, navigate]);

  if (loading || !user || !user.email_confirmed_at) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-background text-muted-foreground">
        {t("common.loading")}
      </main>
    );
  }

  return (
    <WhiteLabelProvider>
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <AppSidebar />
        <div className="flex-1 flex flex-col min-w-0">
          <PaymentTestModeBanner />
          <header className="h-14 border-b border-border/50 sticky top-0 z-30 bg-background/80 backdrop-blur-xl flex items-center justify-between px-4 gap-4">
            <div className="flex items-center gap-2 min-w-0">
              <SidebarTrigger />
              <span className="text-sm text-muted-foreground truncate hidden sm:inline">
                {user.email}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <NotificationsBell />
              <Button
                variant="ghost"
                size="sm"
                onClick={async () => {
                  await signOut();
                  navigate({ to: "/auth" });
                }}
              >
                <LogOut className="w-4 h-4 mr-2" /> {t("app.signOut")}
              </Button>
            </div>
          </header>
          <main className="flex-1 overflow-x-hidden">
            <Outlet />
          </main>
          <AccountTypeOnboarding />
          <WelcomeOnboarding />
          <AssistantChat />

        </div>
      </div>
    </SidebarProvider>
    </WhiteLabelProvider>
  );
}
